import React, {useState, useEffect} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {client} from '../utils/api-client'
import {useAsync} from '../utils/use-async'
import logo from '../assets/Thryv.png'
import {toast} from 'react-toastify'
import {Button, Input} from '@thryvlabs/maverick'
import whiteLettersLogo from '../assets/thryv-logo-white-letters.svg'

import '../styles/formScreensStyles.css'
import '../styles/accountDetails.css'
import {useSelector} from 'react-redux'

// Same circle as the catalog plan cards' radio in the core OOE app
const RadioCircle = ({active}) => (
  <svg
    width="18"
    height="18"
    viewBox="-1 -1 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{flexShrink: 0, display: 'block'}}
    aria-hidden="true"
  >
    <rect
      x="0.5"
      y="0.5"
      width="15"
      height="15"
      rx="7.5"
      fill={active ? '#F4F6FB' : 'white'}
      stroke="#A5B7F6"
    />
    {active && <circle cx="8" cy="8" r="4.5" fill="#0049D4" />}
  </svg>
)

export const ScreenContainer = ({children, className = '', ...props}) => (
  <div className={`screen-container ${className}`} {...props}>
    {children}
  </div>
)

export const Container = ({children, className = '', ...props}) => (
  <div className={`container ${className}`} {...props}>
    {children}
  </div>
)

export const BlueGradientBackground = ({
  showLogo = true,
  backgroundHexColor = 'linear-gradient(to bottom, #171d37, #6474a9)',
  width = '100%',
  className = '',
}) => {
  return (
    <section
      className={`blue-gradient-bg ${className}`}
      style={{
        background: backgroundHexColor,
        width,
      }}
    >
      <div>
        {showLogo && (
          <img
            src={whiteLettersLogo}
            alt="Logo"
            style={{
              maxWidth: '98px',
              marginTop: '24px',
              marginLeft: '24px',
            }}
          />
        )}
      </div>
    </section>
  )
}

export const ThryvLogoBlackLetters = () => {
  return (
    <img
      src={logo}
      alt="Logo"
      style={{
        maxWidth: '98px',
        marginBottom: '12px',
      }}
    />
  )
}

const AuthForm = () => {
  const [method, setMethod] = useState('sms')
  const [smsValue, setSmsValue] = useState('')
  const [emailValue, setEmailValue] = useState('')

  const {isMobileViewEnable} = useSelector(state => {
    return state.screenView
  })

  const {run} = useAsync({
    status: 'pending',
  })
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const id = params.get('LinkId')

  const defaultStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    border: '1px solid #e0e0e0',
  }

  const checkedStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    border: '1px solid #0070f3',
  }

  const optionStyle = {
    boxShadow: '0px 2px 6.1px 0px #0000002b',
    borderRadius: '6px',
    padding: '16px 20px',
    cursor: 'pointer',
    display: 'flex',
    transition: 'border-color 0.15s ease',
  }

  const hiddenRadioStyle = {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: 'none',
  }

  const labelStyle = {
    fontFamily: "'Open Sans', Arial, sans-serif",
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0px',
    color: '#000',
    margin: '0px',
  }

  const apiUrl = 'keappartner/sendotp'
  const navigate = useNavigate()

  useEffect(() => {
    const getKeapActivityFuncTrigger = async () => {
      try {
        if (id) {
          let data = {
            linkId: id,
            currentScreen: 'home',
            baseUrl: window.location.href,
          }
          const response = await run(
            client('keappartner/updatePartnerActivity', {data, method: 'PUT'}),
          )

          if (response && response.error) {
            toast.error(response.error)
          }
        }
      } catch (err) {
        toast.error(err?.error || err?.message || 'An error occurred')
      }
    }
    getKeapActivityFuncTrigger()
  }, [])
  const validateEmail = email => {
    const regex = /^[a-zA-Z0-9._+-]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }
  const validatePhone = phone => {
    const regex = /^\+?[0-9]{10,15}$/ // Accepts an optional '+' at the beginning, followed by 10 to 15 digits
    return regex.test(phone)
  }

  const handleSubmit = async () => {
    if (method === 'sms') {
      if (!validatePhone(smsValue)) {
        toast.warn('Phone number is not correct')
        return
      }
      try {
        const data = {
          keapPartnerRecordId: id || 4,
          method: 'sms',
          phoneNum: smsValue,
        }
        await run(client(apiUrl, {data, method: 'POST'}))
        toast.success(
          'One-time code has been sent. Please check your text messages.',
        )
        navigate(`/verifytoken?LinkId=${id}`)
      } catch (err) {
        toast.error(
          err?.error ||
            err?.message ||
            'Failed to send verification code. Please try again.',
        )
      }
    } else if (method === 'email') {
      if (!validateEmail(emailValue)) {
        toast.error('Please use valid email ID to send the OTP')
        return
      }
      try {
        const data = {
          keapPartnerRecordId: id || 4,
          method: 'email',
          emailId: emailValue,
        }
        await run(client(apiUrl, {data, method: 'POST'}))
        toast.success('One-time code has been sent. Please check your email.')
        navigate(`/verifytoken?LinkId=${id}`)
      } catch (err) {
        toast.error(
          err?.error ||
            err?.message ||
            'Failed to send verification code. Please try again.',
        )
      }
    }
  }

  return (
    <>
      <BlueGradientBackground />
      <section
        className={isMobileViewEnable ? 'auth-form-m' : 'auth-form'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
          zIndex: 1,
          overflow: 'hidden',
        }}
      >
        {/* write below code for mobile view */}
        <div
          className={isMobileViewEnable ? 'auth-card-m' : 'auth-card'}
          style={{
            backgroundColor: 'white',
            margin: 'auto',
            border: '1px solid #ECEEF1',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.25)',
            gap: '24px',
            ...(isMobileViewEnable
              ? {padding: '20px 0px', width: '80%'}
              : {padding: '20px', width: '100%', maxWidth: '520px'}),
          }}
        >
          <section
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <ThryvLogoBlackLetters />
            <p
              style={{
                fontFamily: 'Montserrat',
                fontSize: '24px',
                fontWeight: '600',
                color: '#000',
                margin: '0px',
                padding: '0px 20px',
              }}
              className="text-center"
            >
              Thank you for choosing Thryv!
            </p>
            <p
              style={{
                fontFamily: 'Open Sans',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '22px',
                color: '#4D4D4D',
                margin: '0px',
                padding: '0px 20px',
              }}
              className="text-center"
            >
              Help us to keep your data safe. To verify your identity, we need
              to send you a one-time code. How would you like to receive it?
              Message and data rates from your carrier may apply.
            </p>
          </section>
          <section
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '100%',
              padding: '0px 20px',
            }}
          >
            <div
              style={{
                ...optionStyle,
                ...(method === 'sms' ? checkedStyle : defaultStyle),
                display: 'flex',
                gap: '12px',
              }}
              onClick={() => setMethod('sms')}
            >
              <div
                className="flex"
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <input
                  type="radio"
                  id="sms"
                  name="sms"
                  value="sms"
                  checked={method === 'sms'}
                  onChange={() => setMethod('sms')}
                  style={hiddenRadioStyle}
                />
                <RadioCircle active={method === 'sms'} />
                <label
                  htmlFor="sms"
                  className="cursor-pointer"
                  style={labelStyle}
                >
                  <span
                    style={{
                      marginLeft: '10px',
                    }}
                  >
                    Text Message
                  </span>
                </label>
              </div>
              {method === 'sms' && (
                <Input
                  variant="default"
                  type="text"
                  placeholder={'Phone number'}
                  style={{
                    marginLeft: '28px',
                  }}
                  value={smsValue}
                  onChange={e => setSmsValue(e.target.value)}
                />
              )}
            </div>
            <div
              style={{
                ...optionStyle,
                ...(method === 'email' ? checkedStyle : defaultStyle),
                display: 'flex',
                gap: '12px',
              }}
              onClick={() => setMethod('email')}
            >
              <div
                className="flex"
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <input
                  type="radio"
                  id="email"
                  name="email"
                  value="email"
                  checked={method === 'email'}
                  onChange={() => setMethod('email')}
                  style={hiddenRadioStyle}
                />
                <RadioCircle active={method === 'email'} />
                <label
                  htmlFor="email"
                  className="cursor-pointer"
                  style={labelStyle}
                >
                  <span
                    style={{
                      marginLeft: '10px',
                    }}
                  >
                    Email
                  </span>
                </label>
              </div>
              {method === 'email' && (
                <div className="d-flex flex-column align-items-center">
                  <Input
                    variant="default"
                    type="text"
                    placeholder={'Email'}
                    value={emailValue}
                    style={{
                      marginLeft: '28px',
                    }}
                    onChange={e => setEmailValue(e.target.value)}
                  />
                </div>
              )}
            </div>
          </section>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '0px 20px',
            }}
          >
            <Button
              variant="primary"
              className="ad-continue ad-schedule-action"
              onClick={handleSubmit}
            >
              Send
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}

export default AuthForm
